import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Chart from "react-apexcharts"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faArrowUp,
	faArrowDown,
	faMinus,
	faChevronLeft,
	faChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import {
	fetchStatsV2Cockpit,
	fetchStatsV2Tendances,
} from "../../../store/actions/statsV2Actions"
import { generalStyles as gs } from "../../styles/styles"
import { COLORS } from "../../../utilities/Constants"
import { useRights } from "../../../hooks/rights"
import { formatNumber } from "../../../utilities/Addons"
import { couleurMois, libelleMois, pertesStockKpi } from "./pertesStock"

// ── Helpers date (code navigateur → Date natif OK) ───────────────────────────
const pad = (n) => String(n).padStart(2, "0")
const toISO = (d) =>
	`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

const MOIS_FR = [
	"janvier",
	"février",
	"mars",
	"avril",
	"mai",
	"juin",
	"juillet",
	"août",
	"septembre",
	"octobre",
	"novembre",
	"décembre",
]
const MOIS_COURT = [
	"janv.",
	"févr.",
	"mars",
	"avr.",
	"mai",
	"juin",
	"juil.",
	"août",
	"sept.",
	"oct.",
	"nov.",
	"déc.",
]

// "2024-06" → "juin 24"
const fmtMonth = (key) => {
	const [y, m] = key.split("-")
	return `${MOIS_COURT[parseInt(m, 10) - 1]} ${y.slice(2)}`
}

// Libellés d'axe compacts (jan, fév, mar…) — "2024-06" → "jun"
const MOIS_MINI = [
	"jan",
	"fév",
	"mar",
	"avr",
	"mai",
	"jun",
	"jul",
	"aoû",
	"sep",
	"oct",
	"nov",
	"déc",
]
const miniMonth = (key) => MOIS_MINI[parseInt(key.split("-")[1], 10) - 1]

// Fenêtre [s, e] + libellé, pour une granularité (preset) autour d'une date d'ancrage.
const periodWindow = (preset, anchor) => {
	const y = anchor.getFullYear()
	const m = anchor.getMonth()
	switch (preset) {
		case "trimestre": {
			const q = Math.floor(m / 3)
			return {
				s: new Date(y, q * 3, 1),
				e: new Date(y, q * 3 + 3, 0),
				label: `T${q + 1} ${y}`,
			}
		}
		case "annee":
			return {
				s: new Date(y, 0, 1),
				e: new Date(y, 11, 31),
				label: `${y}`,
			}
		case "douze": {
			const s = new Date(y, m - 11, 1)
			const e = new Date(y, m + 1, 0)
			return {
				s,
				e,
				label: `${MOIS_COURT[s.getMonth()]} ${s.getFullYear()} – ${MOIS_COURT[e.getMonth()]} ${e.getFullYear()}`,
			}
		}
		case "mois":
		default:
			return {
				s: new Date(y, m, 1),
				e: new Date(y, m + 1, 0),
				label: `${MOIS_FR[m]} ${y}`,
			}
	}
}

// Décale la date d'ancrage d'un cran, selon la granularité.
const shiftAnchor = (preset, anchor, dir) => {
	const d = new Date(anchor)
	if (preset === "trimestre") d.setMonth(d.getMonth() + dir * 3)
	else if (preset === "annee") d.setFullYear(d.getFullYear() + dir)
	else if (preset === "douze") d.setMonth(d.getMonth() + dir * 12)
	else d.setMonth(d.getMonth() + dir)
	return d
}

const DOMAINE_META = [
	{ key: "depannage", label: "Dépannage", color: COLORS.accent },
	{ key: "atelier", label: "Atelier", color: COLORS.warning },
	{ key: "location", label: "Location", color: COLORS.success },
]

const chf = (v) => formatNumber(v || 0)

// ── Δ badge (vert = mieux, rouge = moins bien ; invert pour les impayés) ─────
const Delta = ({ pc, invert = false }) => {
	if (pc === null || pc === undefined)
		return (
			<span style={{ ...gs.statSub, color: COLORS.textLight }}>
				<FontAwesomeIcon icon={faMinus} /> n/a
			</span>
		)
	const positif = pc >= 0
	const bon = invert ? !positif : positif
	const color =
		pc === 0 ? COLORS.textMuted : bon ? COLORS.success : COLORS.danger
	const icon = pc === 0 ? faMinus : positif ? faArrowUp : faArrowDown
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 4,
				fontSize: 12,
				fontWeight: 600,
				color,
			}}
		>
			<FontAwesomeIcon icon={icon} style={{ fontSize: 10 }} />
			{Math.abs(pc).toFixed(1)}%
		</span>
	)
}

const KpiCard = ({ label, value, unit, sub, delta, accent, highlight }) => (
	<div style={highlight ? styles.heroCardStrong : styles.heroCard}>
		<div
			style={{
				...gs.statLabel,
				color: highlight ? "#cfc9c2" : COLORS.textLight,
			}}
		>
			{label}
		</div>
		<div style={styles.heroValueRow}>
			<span
				style={{
					...gs.statValue,
					color: highlight ? "#fff" : accent || COLORS.text,
					fontSize: 26,
				}}
			>
				{value}
			</span>
			{unit && <span style={styles.unit}>{unit}</span>}
		</div>
		<div style={styles.heroSubRow}>
			{delta}
			{sub && (
				<span
					style={{
						...gs.statSub,
						color: highlight ? "#b8b2ab" : COLORS.textMuted,
					}}
				>
					{sub}
				</span>
			)}
		</div>
	</div>
)

// ── Sparkline (12 mois glissants finissant à la période, dérivée des tendances) ─
const Sparkline = ({ data, labels, color, unite }) => (
	<Chart
		type="area"
		height={78}
		series={[{ name: "CA", data }]}
		options={{
			chart: {
				toolbar: { show: false },
				animations: { enabled: false },
				fontFamily: "'DM Sans', sans-serif",
				parentHeightOffset: 0,
			},
			stroke: { curve: "smooth", width: 2 },
			fill: {
				type: "gradient",
				gradient: { opacityFrom: 0.3, opacityTo: 0 },
			},
			colors: [color],
			dataLabels: { enabled: false },
			grid: {
				show: false,
				padding: { left: 4, right: 4, top: 0, bottom: -6 },
			},
			yaxis: { show: false },
			xaxis: {
				categories: labels,
				tickPlacement: "on",
				labels: {
					rotate: 0,
					hideOverlappingLabels: true,
					style: { fontSize: "9px", colors: COLORS.textLight },
				},
				axisBorder: { show: false },
				axisTicks: { show: false },
				tooltip: { enabled: false },
			},
			tooltip: {
				y: {
					formatter: (v) => `${chf(v)} ${unite}`,
					title: { formatter: () => "" },
				},
			},
		}}
	/>
)

// Onglets de la page, un droit par onglet (matrice Excel). Seul « Financières »
// a du contenu aujourd'hui : les 3 autres sont créés avec leur droit déjà
// branché et seront remplis a posteriori (décision Jordan 30.07.2026).
const ONGLETS = [
	{ key: "financier", label: "Financières", right: "stats_financier" },
	{
		key: "interventions",
		label: "Interventions",
		right: "stats_interventions",
	},
	{ key: "mecanique", label: "Mécanique", right: "stats_mecanique" },
	{ key: "location", label: "Location", right: "stats_location" },
]

const StatsV2 = () => {
	const dispatch = useDispatch()
	const { hasRight } = useRights()
	const { cockpit, tendances, fetchCockpit } = useSelector(
		(state) => state.statsV2Info,
	)

	const ongletsVisibles = useMemo(
		() => ONGLETS.filter((o) => hasRight(o.right)),
		[hasRight],
	)
	// L'onglet actif suit les droits : un utilisateur sans « Financières »
	// atterrit sur le premier onglet qu'il a réellement.
	const [onglet, setOnglet] = useState("financier")
	useEffect(() => {
		if (!ongletsVisibles.length) return
		if (!ongletsVisibles.some((o) => o.key === onglet))
			setOnglet(ongletsVisibles[0].key)
	}, [ongletsVisibles, onglet])

	const [preset, setPreset] = useState("annee")
	const [anchor, setAnchor] = useState(() => new Date())
	const [basis, setBasis] = useState("activite")
	const [comparaison, setComparaison] = useState("yoy") // "prev" | "yoy"

	// Changer de granularité recentre sur la période courante (aujourd'hui).
	const choosePreset = (key) => {
		setPreset(key)
		setAnchor(new Date())
	}
	const nav = (dir) => setAnchor((a) => shiftAnchor(preset, a, dir))

	const win = useMemo(() => periodWindow(preset, anchor), [preset, anchor])
	const dateS = toISO(win.s)
	const dateE = toISO(win.e)
	const anneeFin = useMemo(() => new Date(dateE).getFullYear(), [dateE])
	const anneeDebut = anneeFin - 2

	// Les deux endpoints stats_v2 sont gatés sur `stats_financier` : sans le
	// droit, on ne les appelle pas (sinon 403 + toast à chaque ouverture).
	const peutVoirFinancier = hasRight("stats_financier")

	useEffect(() => {
		if (!peutVoirFinancier) return
		dispatch(fetchStatsV2Cockpit({ dateS, dateE, basis }))
	}, [dispatch, dateS, dateE, basis, peutVoirFinancier])

	useEffect(() => {
		if (!peutVoirFinancier) return
		dispatch(fetchStatsV2Tendances({ anneeDebut, anneeFin, basis }))
	}, [dispatch, anneeDebut, anneeFin, basis, peutVoirFinancier])

	const unite = cockpit?.unite || (basis === "encaissement" ? "TTC" : "HT")
	const deltaKey = comparaison === "yoy" ? "delta_yoy_pc" : "delta_prev_pc"

	// Fenêtre sparkline = 12 mois glissants finissant au mois de fin de la période
	// (ex. « juin 2024 » → juil. 2023 … juin 2024). Le CA de l'année réparti par mois.
	const sparkWin = useMemo(() => {
		if (!tendances)
			return { start: 0, end: 0, labels: [], caption: "CA annuel" }
		const i = tendances.mois.indexOf(dateE.slice(0, 7))
		const end = i < 0 ? tendances.mois.length : i + 1
		const start = Math.max(0, end - 12)
		const keys = tendances.mois.slice(start, end)
		return {
			start,
			end,
			labels: keys.map(miniMonth),
			caption: keys.length
				? `CA annuel · ${fmtMonth(keys[0])} → ${fmtMonth(keys[keys.length - 1])}`
				: "CA annuel",
		}
	}, [tendances, dateE])

	const spark = (key) =>
		tendances
			? tendances.series[key].slice(sparkWin.start, sparkWin.end)
			: []

	const moisLabels = useMemo(() => {
		if (!tendances) return []
		return tendances.mois.map((m) => {
			const [y, mo] = m.split("-")
			return `${mo}/${y.slice(2)}`
		})
	}, [tendances])

	return (
		<div style={gs.page}>
			<div style={styles.shell}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Statistiques</div>
						<div style={gs.title}>Cockpit direction</div>
					</div>
					<div style={styles.periodNav}>
						<button
							type="button"
							style={styles.navBtn}
							title="Période précédente"
							onClick={() => nav(-1)}
						>
							<FontAwesomeIcon icon={faChevronLeft} />
						</button>
						<span style={styles.periodLabel}>{win.label}</span>
						<button
							type="button"
							style={styles.navBtn}
							title="Période suivante"
							onClick={() => nav(1)}
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					</div>
				</div>

				{/* Onglets (un droit par onglet, cf. ONGLETS) */}
				{ongletsVisibles.length > 1 && (
					<div style={{ ...styles.segGroup, marginBottom: 18 }}>
						{ongletsVisibles.map((o) => (
							<button
								key={o.key}
								type="button"
								style={gs.segment(onglet === o.key)}
								onClick={() => setOnglet(o.key)}
							>
								{o.label}
							</button>
						))}
					</div>
				)}

				{onglet === "financier" ? (
					<>
						{/* Contrôles */}
						<div style={styles.controls}>
							<div style={styles.segGroup}>
								{[
									["mois", "Mois"],
									["trimestre", "Trimestre"],
									["annee", "Année"],
									["douze", "12 mois"],
								].map(([k, lbl]) => (
									<button
										key={k}
										type="button"
										style={gs.segment(preset === k)}
										onClick={() => choosePreset(k)}
									>
										{lbl}
									</button>
								))}
							</div>
							<div style={styles.segGroup}>
								{[
									["activite", "Activité"],
									["encaissement", "Encaissé"],
								].map(([k, lbl]) => (
									<button
										key={k}
										type="button"
										style={gs.segment(basis === k)}
										onClick={() => setBasis(k)}
									>
										{lbl}
									</button>
								))}
							</div>
							{/* Pour Année et 12 mois, « vs préc. » et « vs N-1 » ciblent la
					    même fenêtre (l'année d'avant) → toggle masqué. */}
							{(preset === "mois" || preset === "trimestre") && (
								<div style={styles.segGroup}>
									{[
										["prev", "vs préc."],
										["yoy", "vs N-1"],
									].map(([k, lbl]) => (
										<button
											key={k}
											type="button"
											style={gs.segment(
												comparaison === k,
											)}
											onClick={() => setComparaison(k)}
										>
											{lbl}
										</button>
									))}
								</div>
							)}
						</div>

						{fetchCockpit.loading && !cockpit ? (
							<div style={styles.loading}>
								<FontAwesomeIcon icon={faSync} spin />{" "}
								Chargement…
							</div>
						) : cockpit ? (
							<>
								{/* Ligne héros */}
								<div style={styles.heroGrid}>
									<KpiCard
										label={`CA total · ${unite}`}
										value={chf(cockpit.total.ca)}
										sub={`${cockpit.total.count} interventions`}
										delta={
											<Delta
												pc={cockpit.total[deltaKey]}
											/>
										}
										highlight
									/>
									<KpiCard
										label="Marge atelier"
										value={chf(
											cockpit.marge_atelier.marge_ht,
										)}
										unit={`${cockpit.marge_atelier.marge_pc}%`}
										sub={`coût ${chf(cockpit.marge_atelier.cout_ht)}`}
										delta={
											<Delta
												pc={
													cockpit.marge_atelier[
														comparaison === "yoy"
															? "yoy_marge_pc"
															: "prev_marge_pc"
													] != null
														? cockpit.marge_atelier
																.marge_pc -
															cockpit
																.marge_atelier[
																comparaison ===
																"yoy"
																	? "yoy_marge_pc"
																	: "prev_marge_pc"
															]
														: null
												}
											/>
										}
									/>
									<KpiCard
										label="Impayés en cours"
										value={chf(
											cockpit.tresorerie.impayes.total,
										)}
										accent={COLORS.danger}
										sub={`dont ${chf(cockpit.tresorerie.impayes.j60_plus)} à +60j`}
									/>
									{cockpit.pertes_stock && (
										<KpiCard
											// Jordan 27.10 : net négatif = gain
											// d'inventaire (pertesStock.js).
											label={
												pertesStockKpi(
													cockpit.pertes_stock.net_ht,
												).label
											}
											value={chf(
												pertesStockKpi(
													cockpit.pertes_stock.net_ht,
												).valeur,
											)}
											unit="HT"
											accent={
												cockpit.pertes_stock.net_ht < 0
													? COLORS.success
													: cockpit.pertes_stock
																.net_ht > 0
														? COLORS.danger
														: COLORS.text
											}
											sub={`pertes ${chf(cockpit.pertes_stock.pertes_ht)} · récup ${chf(
												Math.abs(
													cockpit.pertes_stock
														.recup_ht,
												),
											)} · écarts ${chf(cockpit.pertes_stock.ecarts_ht)}`}
										/>
									)}
									<KpiCard
										label="Volume"
										value={cockpit.total.count}
										sub={DOMAINE_META.map(
											(d) =>
												`${cockpit.domaines[d.key].count} ${d.label.slice(0, 3).toLowerCase()}.`,
										).join(" · ")}
									/>
								</div>

								{/* Cartes domaine + sparklines */}
								<div style={styles.domGrid}>
									{DOMAINE_META.map((d) => {
										const c = cockpit.domaines[d.key]
										return (
											<div
												key={d.key}
												style={styles.domCard}
											>
												<div style={styles.domHead}>
													<span
														style={{
															...styles.dot,
															backgroundColor:
																d.color,
														}}
													/>
													<span
														style={styles.domLabel}
													>
														{d.label}
													</span>
													<span
														style={{
															marginLeft: "auto",
														}}
													>
														<Delta
															pc={c[deltaKey]}
														/>
													</span>
												</div>
												<div style={styles.domValueRow}>
													<span style={gs.statValue}>
														{chf(c.ca)}
													</span>
													<span style={styles.unit}>
														{unite}
													</span>
												</div>
												<div style={gs.statSub}>
													{c.count} · moyenne / fiche{" "}
													{chf(c.panier_moyen)}
												</div>
												<div style={{ marginTop: 10 }}>
													<div
														style={
															styles.sparkCaption
														}
													>
														{sparkWin.caption}
													</div>
													<Sparkline
														data={spark(d.key)}
														labels={sparkWin.labels}
														color={d.color}
														unite={unite}
													/>
												</div>
											</div>
										)
									})}
								</div>

								{/* Tendances mensuelles */}
								<div style={styles.card}>
									<div style={styles.cardTitle}>
										Chiffre d'affaires mensuel · {unite}
									</div>
									{tendances && (
										<Chart
											type="bar"
											height={320}
											series={DOMAINE_META.map((d) => ({
												name: d.label,
												data: tendances.series[d.key],
											}))}
											options={{
												chart: {
													stacked: true,
													toolbar: { show: false },
													fontFamily:
														"'DM Sans', sans-serif",
												},
												colors: DOMAINE_META.map(
													(d) => d.color,
												),
												xaxis: {
													categories: moisLabels,
													tickAmount: 12,
												},
												yaxis: {
													labels: {
														formatter: (v) =>
															`${Math.round(v / 1000)}k`,
													},
												},
												plotOptions: {
													bar: { columnWidth: "60%" },
												},
												dataLabels: { enabled: false },
												legend: {
													position: "top",
													horizontalAlign: "left",
												},
												grid: {
													borderColor: COLORS.border,
												},
												tooltip: {
													shared: true,
													intersect: false,
													y: {
														formatter: (v) =>
															`${chf(v)} ${unite}`,
													},
												},
											}}
										/>
									)}
								</div>

								<div style={styles.bottomGrid}>
									{/* Marge % atelier */}
									<div style={styles.card}>
										<div style={styles.cardTitle}>
											Marge % atelier
										</div>
										{tendances && (
											<Chart
												type="line"
												height={220}
												series={[
													{
														name: "Marge %",
														data: tendances.marge_pc_atelier,
													},
												]}
												options={{
													chart: {
														toolbar: {
															show: false,
														},
														fontFamily:
															"'DM Sans', sans-serif",
													},
													colors: [COLORS.warning],
													stroke: {
														curve: "smooth",
														width: 2,
													},
													xaxis: {
														categories: moisLabels,
														tickAmount: 8,
													},
													yaxis: {
														labels: {
															formatter: (v) =>
																`${Math.round(v)}%`,
														},
													},
													dataLabels: {
														enabled: false,
													},
													grid: {
														borderColor:
															COLORS.border,
													},
												}}
											/>
										)}
									</div>

									{/* Marge HT atelier (€) */}
									<div style={styles.card}>
										<div style={styles.cardTitle}>
											Marge HT atelier
										</div>
										{tendances && (
											<Chart
												type="bar"
												height={220}
												series={[
													{
														name: "Marge HT",
														data: tendances.marge_ht_atelier,
													},
												]}
												options={{
													chart: {
														toolbar: {
															show: false,
														},
														fontFamily:
															"'DM Sans', sans-serif",
													},
													colors: [COLORS.warning],
													plotOptions: {
														bar: {
															columnWidth: "60%",
															borderRadius: 3,
														},
													},
													xaxis: {
														categories: moisLabels,
														tickAmount: 8,
													},
													yaxis: {
														labels: {
															formatter: (v) =>
																`${Math.round(v / 1000)}k`,
														},
													},
													dataLabels: {
														enabled: false,
													},
													grid: {
														borderColor:
															COLORS.border,
													},
													tooltip: {
														y: {
															formatter: (v) =>
																`${chf(v)} HT`,
														},
													},
												}}
											/>
										)}
									</div>

									{/* Coûts internes SVP (€) : 3 composantes empilées */}
									<div style={styles.card}>
										<div style={styles.cardTitle}>
											Coûts internes SVP
										</div>
										{tendances && (
											<Chart
												type="bar"
												height={240}
												series={[
													{
														name: "Fiches SVP",
														data: tendances
															.cout_interne_svp
															.fiches,
													},
													{
														name: "Articles virtuels",
														data: tendances
															.cout_interne_svp
															.articles_virtuels,
													},
													{
														name: "Master (perte)",
														data: tendances
															.cout_interne_svp
															.master,
													},
												]}
												options={{
													chart: {
														stacked: true,
														toolbar: {
															show: false,
														},
														fontFamily:
															"'DM Sans', sans-serif",
													},
													colors: [
														COLORS.textMuted,
														COLORS.accent,
														COLORS.danger,
													],
													plotOptions: {
														bar: {
															columnWidth: "60%",
														},
													},
													xaxis: {
														categories: moisLabels,
														tickAmount: 8,
													},
													yaxis: {
														labels: {
															formatter: (v) =>
																`${Math.round(v / 1000)}k`,
														},
													},
													dataLabels: {
														enabled: false,
													},
													legend: {
														position: "top",
														horizontalAlign: "left",
														fontSize: "11px",
													},
													grid: {
														borderColor:
															COLORS.border,
													},
													tooltip: {
														shared: true,
														intersect: false,
														y: {
															formatter: (v) =>
																`${chf(v)} HT`,
														},
													},
												}}
											/>
										)}
									</div>

									{/* Pertes stock (corrections manuelles, net des récupérations) */}
									<div style={styles.card}>
										<div style={styles.cardTitle}>
											Pertes stock
										</div>
										{tendances &&
											tendances.pertes_stock && (
												<Chart
													type="bar"
													height={240}
													series={[
														{
															name: "Pertes (net)",
															data: tendances.pertes_stock,
														},
													]}
													options={{
														chart: {
															toolbar: {
																show: false,
															},
															fontFamily:
																"'DM Sans', sans-serif",
														},
														// Jordan 27.10 : mois en gain
														// d'inventaire (net < 0) en vert.
														colors: [
															({ value }) =>
																couleurMois(
																	value,
																	{
																		gain: COLORS.success,
																		perte: COLORS.danger,
																	},
																),
														],
														plotOptions: {
															bar: {
																columnWidth:
																	"60%",
																borderRadius: 3,
															},
														},
														xaxis: {
															categories:
																moisLabels,
															tickAmount: 8,
														},
														yaxis: {
															labels: {
																formatter: (
																	v,
																) =>
																	`${Math.round(v)}`,
															},
														},
														dataLabels: {
															enabled: false,
														},
														grid: {
															borderColor:
																COLORS.border,
														},
														tooltip: {
															y: {
																formatter: (
																	v,
																) =>
																	`${chf(Math.abs(v))} HT`,
																title: {
																	formatter: (
																		_,
																		{
																			series,
																			seriesIndex,
																			dataPointIndex,
																		},
																	) =>
																		`${libelleMois(
																			series[
																				seriesIndex
																			][
																				dataPointIndex
																			],
																		)} :`,
																},
															},
														},
													}}
												/>
											)}
									</div>
								</div>
							</>
						) : (
							<div style={styles.loading}>Aucune donnée.</div>
						)}
					</>
				) : (
					<div style={styles.loading}>
						Onglet en construction — son contenu sera défini avec
						Jordan.
					</div>
				)}
			</div>
		</div>
	)
}

const styles = {
	shell: { width: "100%", maxWidth: 1200, margin: "0 auto" },
	controls: {
		display: "flex",
		flexWrap: "wrap",
		gap: 12,
		alignItems: "center",
		marginBottom: 24,
	},
	segGroup: { ...gs.segmented, marginBottom: 0 },
	periodNav: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: "6px 8px",
	},
	navBtn: {
		width: 34,
		height: 34,
		borderRadius: 9,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 13,
	},
	periodLabel: {
		minWidth: 150,
		textAlign: "center",
		fontSize: 16,
		fontWeight: 600,
		color: COLORS.text,
		textTransform: "capitalize",
		fontVariantNumeric: "tabular-nums",
	},
	loading: {
		padding: "60px 0",
		textAlign: "center",
		color: COLORS.textMuted,
	},
	heroGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
		gap: 14,
		marginBottom: 16,
	},
	heroCard: { ...gs.statCard, padding: "16px 20px", gap: 6 },
	heroCardStrong: { ...gs.totalHighlight, padding: "16px 20px", gap: 6 },
	heroValueRow: { display: "flex", alignItems: "baseline", gap: 8 },
	heroSubRow: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		marginTop: 2,
	},
	unit: { fontSize: 12, color: COLORS.textLight, fontWeight: 500 },
	domGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
		gap: 14,
		marginBottom: 16,
	},
	domCard: { ...gs.statCard, padding: "16px 18px" },
	sparkCaption: {
		fontSize: 10,
		fontWeight: 500,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.05em",
		marginBottom: 2,
	},
	domHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
	domLabel: { fontSize: 14, fontWeight: 600, color: COLORS.text },
	domValueRow: { display: "flex", alignItems: "baseline", gap: 6 },
	dot: { width: 9, height: 9, borderRadius: 99, flexShrink: 0 },
	card: {
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 14,
		padding: 18,
		marginBottom: 16,
	},
	cardTitle: {
		fontSize: 13,
		fontWeight: 600,
		color: COLORS.text,
		marginBottom: 12,
	},
	bottomGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
		gap: 16,
	},
}

export default StatsV2
